import { Workbook, type Cell } from 'exceljs';
import type { TFunction } from 'i18next';
import { DateTime as DT } from 'luxon';
import {
    getBaseCurrencyCode,
    getCurrencyCode,
    getIsLocalCurrencyMode,
} from 'utils/helpers/currency/currencyHelpers';

export type ExportCell = string | number | Date | null;

export type ExportColumnKind = 'text' | 'money' | 'number' | 'date';

export interface ExportColumn {
    key: string;
    header: string;
    width: number;
    kind?: ExportColumnKind;
}

export interface ExportHeaderLine {
    label: string;
    value: string;
}

export interface ExportReportWorkbookParams {
    filePrefix: string;
    sheetName: string;
    title: string;
    headerLines: ExportHeaderLine[];
    columns: ExportColumn[];
    rows: Record<string, ExportCell>[];
    totalLabel: string;
}

const MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const columnLetter = (index: number): string => {
    let n = index;
    let letters = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        letters = String.fromCharCode(65 + rem) + letters;
        n = Math.floor((n - 1) / 26);
    }
    return letters;
};

const sanitizeSheetName = (name: string): string => {
    const cleaned = name.replace(/[\\/*?:[\]]/g, ' ').trim();
    return (cleaned || 'Report').slice(0, 31);
};

const applyMoneyFormat = (cell: Cell, kind: ExportColumnKind | undefined) => {
    if (kind === 'money') cell.numFmt = '#,##0.00';
    if (kind === 'number') cell.numFmt = '#,##0';
    if (kind === 'date') cell.numFmt = 'dd.mm.yyyy';
    if (kind === 'money' || kind === 'number') cell.alignment = { horizontal: 'right' };
};

export const roundExportMoney = (value: number): number =>
    Math.round((Number(value) || 0) * 100) / 100;

export const exportDate = (ms: number | null | undefined): Date | null => {
    const value = Number(ms || 0);
    if (!value) return null;
    const dt = DT.fromMillis(value);
    if (!dt.isValid) return null;
    return new Date(Date.UTC(dt.year, dt.month - 1, dt.day));
};

export const formatExportPeriod = (
    periodTitle: string,
    dates: [number, number] | null,
): string => {
    if (!dates?.[0] || !dates?.[1]) return periodTitle;
    const from = DT.fromMillis(dates[0]).toFormat('dd.MM.yyyy');
    const to = DT.fromMillis(dates[1]).toFormat('dd.MM.yyyy');
    return `${periodTitle}: ${from} — ${to}`;
};

export const isExportLocalMode = (rate: number): boolean =>
    getIsLocalCurrencyMode(Number(rate) || 1);

export const moneyColumns = (
    baseKey: string,
    localKey: string,
    label: string,
    rate: number,
): ExportColumn[] => {
    const localMode = isExportLocalMode(rate);
    const columns: ExportColumn[] = [
        {
            key: baseKey,
            header: `${label}, ${localMode ? getCurrencyCode() : getBaseCurrencyCode()}`,
            width: 18,
            kind: 'money',
        },
    ];
    if (!localMode) {
        columns.push({
            key: localKey,
            header: `${label}, ${getCurrencyCode()}`,
            width: 18,
            kind: 'money',
        });
    }
    return columns;
};

export const yesNoLabel = (value: boolean, t: TFunction): string =>
    value ? t('common:yes') : t('common:no');

export const exportReportWorkbook = async ({
    filePrefix,
    sheetName,
    title,
    headerLines,
    columns,
    rows,
    totalLabel,
}: ExportReportWorkbookParams): Promise<void> => {
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet(sanitizeSheetName(sheetName));
    const colCount = Math.max(columns.length, 1);

    if (colCount > 1) sheet.mergeCells(1, 1, 1, colCount);
    const titleCell = sheet.getCell(1, 1);
    titleCell.value = title;
    titleCell.font = { bold: true, size: 16 };
    sheet.getRow(1).height = 24;

    const headerWidth = columns.reduce((sum, column) => sum + column.width, 0) || 40;
    let rowIndex = 2;
    for (const line of headerLines) {
        if (colCount > 1) sheet.mergeCells(rowIndex, 1, rowIndex, colCount);
        const cell = sheet.getCell(rowIndex, 1);
        cell.value = {
            richText: [
                { font: { bold: true }, text: `${line.label}: ` },
                { font: { bold: false }, text: line.value },
            ],
        };
        cell.alignment = { wrapText: true, vertical: 'middle' };
        const lineCount = Math.max(1, Math.ceil((line.label.length + line.value.length + 2) / headerWidth));
        sheet.getRow(rowIndex).height = Math.max(18, lineCount * 16);
        rowIndex += 1;
    }

    rowIndex += 1;
    const headerRowIndex = rowIndex;

    columns.forEach((column, index) => {
        const cell = sheet.getCell(headerRowIndex, index + 1);
        cell.value = column.header;
        cell.font = { bold: true };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF8F9FA' },
        };
        cell.border = {
            bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } },
        };
        sheet.getColumn(index + 1).width = column.width;
        if (column.kind === 'money' || column.kind === 'number') {
            cell.alignment = { horizontal: 'right' };
        }
    });

    rows.forEach((data, offset) => {
        const dataRow = headerRowIndex + 1 + offset;
        columns.forEach((column, index) => {
            const cell = sheet.getCell(dataRow, index + 1);
            const raw = data[column.key];
            if (raw == null || raw === '') {
                cell.value = column.kind === 'money' || column.kind === 'number' ? 0 : '';
            } else {
                cell.value = raw;
            }
            applyMoneyFormat(cell, column.kind);
        });
    });

    const firstDataRow = headerRowIndex + 1;
    const lastDataRow = headerRowIndex + rows.length;

    sheet.autoFilter = {
        from: { row: headerRowIndex, column: 1 },
        to: { row: lastDataRow, column: columns.length },
    };

    sheet.views = [{ state: 'frozen', ySplit: headerRowIndex, xSplit: 0 }];

    const totalRow = lastDataRow + 1;
    const totalBorder = {
        top: { style: 'thin' as const, color: { argb: 'FFDEE2E6' } },
    };

    columns.forEach((column, index) => {
        const cell = sheet.getCell(totalRow, index + 1);
        cell.font = { bold: true };
        cell.border = totalBorder;
        if (index === 0) {
            cell.value = totalLabel;
            return;
        }
        if (column.kind !== 'money' && column.kind !== 'number') return;
        const letter = columnLetter(index + 1);
        cell.value = { formula: `SUM(${letter}${firstDataRow}:${letter}${lastDataRow})` };
        applyMoneyFormat(cell, column.kind);
    });

    const stamp = DT.now().toFormat('yyyyMMdd-HHmm');
    const buffer = await workbook.xlsx.writeBuffer();
    downloadBlob(new Blob([buffer], { type: MIME }), `${filePrefix}-${stamp}.xlsx`);
};
